<template>
  <a-row class="project-manager-layout">
    <a-col flex="250px" class="project-manager-layout__left-side">
      <SideBar :items="sidebarItems" />
    </a-col>
    <a-col flex="auto" class="project-manager-layout__right-side">
      <div class="project-manager-layout__header">
        <!-- HeaderPartnerAccount back-page-name="project-manager-requests" /-->
        <HeaderPartnerAccount />
      </div>
      <div ref="scrollElement" class="project-manager-layout__content">
        <a-spin
          :spinning="spinning"
          class="custom-spinner"
          style="height: 100vh; top: 20%"
        >
          <slot></slot>
        </a-spin>
      </div>
    </a-col>
  </a-row>
</template>

<script setup>
  import { onBeforeUnmount, onMounted, ref } from 'vue';
  import SideBar from '@/components/sidebar/SideBar';
  import HeaderPartnerAccount from '@/components/header/HeaderPartnerAccount';
  import { ROUTE_NAME_LEADER_TEAM } from '@/constants/route';
  import { useApi } from '@/api/use-api';
  import { STATUS_NEW } from '@/constants/project-team-request';
  import { debounce } from 'lodash-es';
  import { useStore } from 'vuex';
  import { USER_ROLE_LEADER } from '@/api/models/UserRole';

  document.title = 'Цифровой университет. НГУ';

  defineProps({
    spinning: {
      type: Boolean,
      default: false,
    },
  });

  const api = useApi();
  const scrollThreshold = 30;
  const scrollElement = ref(null);
  const emit = defineEmits(['scrolled-to-bottom']);
  const store = useStore();

  const sidebarItems = ref([
    {
      id: 1,
      text: 'Мои портфели',
      path: 'leader-portfolios',
      type: 'portfolios',
      count: 0,
    },
    {
      id: 2,
      text: 'Мои проекты',
      path: 'project-manager-projects',
      hash: '#inwork',
      type: 'projects',
      count: 0,
      badge: null,
    },
    {
      id: 3,
      text: 'Запросы в команду',
      path: ROUTE_NAME_LEADER_TEAM,
      type: 'vacancies',
      count: 0,
    },
    {
      id: 4,
      text: 'Витрина',
      type: 'showcases',
      url: '/showcase',
    },
  ]);

  onMounted(async () => {
    await updateCounters();
    scrollElement.value?.addEventListener('scroll', handleScroll);
  });

  onBeforeUnmount(() => {
    scrollElement.value?.removeEventListener('scroll', handleScroll);
  });

  const handleScroll = debounce(() => {
    const elementHeight = scrollElement.value?.getBoundingClientRect().height;
    const scrollHeight = scrollElement.value?.scrollHeight;
    const scrollBottom = scrollElement.value?.scrollTop + elementHeight;
    if (scrollBottom + scrollThreshold > scrollHeight) {
      emit('scrolled-to-bottom', scrollBottom);
    }
  }, 70);

  const updateCounters = async () => {
    await updateRequestCount();
    await updateTeamRequestCount();
    await updateCompetenceBadge();
  };

  const updateRequestCount = async () => {
    try {
      const response = await api.request.count(USER_ROLE_LEADER);
      sidebarItems.value[1].count = response.count;
      if (response.count > 0) {
        sidebarItems.value[1].path = 'project-manager-requests';
        sidebarItems.value[1].hash = '';
      } else {
        sidebarItems.value[1].path = 'project-manager-projects';
        sidebarItems.value[1].hash = '#inwork';
      }
    } catch (error) {
      console.error('error', error);
    }
  };

  const updateTeamRequestCount = async () => {
    try {
      const response = await api.projectTeamRequest.count(
        new URLSearchParams({
          status: STATUS_NEW,
        })
      );
      sidebarItems.value[2].count = response.count;
    } catch (error) {
      console.error('error', error);
    }
  };

  const updateCompetenceBadge = async () => {
    if (!store.getters.enableCompetencies) return;

    try {
      const response =
        await api.checkpointCompetenceSet.hasUnconfirmedCompetencies();
      sidebarItems.value[1].badge = response.hasUnconfirmedCompetencies
        ? 'К'
        : null;
    } catch (error) {
      console.error('error', error);
    }
  };

  defineExpose({ updateCounters });
</script>

<style lang="scss" scoped>
  @use '@/assets/styles/_colors.scss' as *;

  .project-manager-layout {
    min-height: 100vh;
    flex-wrap: nowrap;

    .project-manager-layout__right-side {
      overflow: hidden;
      height: 100vh;

      .project-manager-layout__header {
        border-bottom: 1px solid $color-bg-secondary-grey;
        background-color: $color-bg-primary-white;
        position: sticky;
        padding: 17px 40px 17px 20px;
        top: 0;
        z-index: 1;
      }

      .project-manager-layout__content {
        overflow-y: auto;
        height: 100%;
        padding: 30px 40px 100px 20px;
      }
    }
  }
</style>
